// ============================================
// IMPORTANT: Polyfills must be loaded FIRST
// This provides compatibility for Legacy Smart TVs
// (LG WebOS 3.x-5.x, Samsung Tizen 2.x-4.x)
// ============================================
import "./polyfills";

import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import "./style/legacy-tv.css"; // Legacy Smart TV CSS fixes
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import HomeScreen from "./app/page";
import AnaVatanPage from "./app/anavatan/page";
import CityContentPage from "./app/anavatan/city/page";
import CategoriesPage from "./app/categories/page";
import ChildCategory from "./app/categories/parent/child/page";
import LoginPage from "./app/login/page";
import MoviePage from "./app/movie/page";
import ProfilePage from "./app/profile/page";
import SearchPage from "./app/search/page";
import SubscriptionPage from "./app/subscription/page";
import MoviePlayer from "./app/watch/page";
import MoftabakhPage from "./app/moftabakh/page";
import ChildGenre from "./app/genre/parent/child/page";
import SeriesPage from "./app/series/page";
import ProgramPage from "./app/program/page";
import LivePage from "./app/live/page";
import OnlineCinemaPage from "./app/online-cinema/page";
import ProfileFavourtiesPage from "./app/profile/favourites";
import ProfileSubscriptionsPage from "./app/profile/subscriptions";
import ProfileWatchHistoryPage from "./app/profile/watchHistory";
import ArtistPage from "./app/artist/page";
import MovieHome from "./app/movie/home";
import SeriesHome from "./app/series/home";
import MusicHome from "./app/music/home";
import TrailerPlayer from "./app/trailer/page";

// PWA and Preload components
import { initializePWA, isOffline, onNetworkStatusChange } from "./utils/pwaManager";
import PreloadSplash from "./components/common/PreloadSplash";
import ForceUpdateModal from "./components/common/ForceUpdateModal";

// Version check
import { BUILD_VERSION } from "./config/default";
import { getCMSApiRoute } from "./utils/api.utils";
import axios from "axios";

// Redux Provider - wrap at root level for loading state management
import StateProvider from "./redux/provider";

// Input mode tracking for mouse vs keyboard focus
import { InputModeProvider } from "./context/InputModeContext";

global.AbortController = window.AbortController;

Number.isInteger =
  Number.isInteger ||
  function (value) {
    return (
      typeof value === "number" &&
      isFinite(value) &&
      Math.floor(value) === value
    );
  };

// Initialize PWA and service worker
initializePWA().then(() => {
  console.log('[App] PWA initialized');
}).catch((error) => {
  console.warn('[App] PWA initialization failed:', error);
});

/**
 * Check if app version is below minimum required
 * @returns {Promise<boolean>} True if update is required
 */
async function checkMinVersion() {
  try {
    const response = await axios.get(getCMSApiRoute("config?device=web_tv"));
    const { data: configData } = response.data;

    const minVersion = parseInt(configData.lastAllowedVer, 10);

    if (isNaN(minVersion)) {
      console.log('[App] No minVersion in config, skipping version check');
      return false;
    }

    console.log('[App] Version check:', { local: BUILD_VERSION, required: minVersion });

    if (BUILD_VERSION < minVersion) {
      console.log('[App] Update required:', BUILD_VERSION, '<', minVersion);
      return true;
    }

    return false;
  } catch (error) {
    console.warn('[App] Version check failed:', error);
    // Don't block app if version check fails
    return false;
  }
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <HomeScreen />,
  },
  {
    path: "/anavatan",
    element: <AnaVatanPage />,
  },
  {
    path: "/anavatan/city",
    element: <CityContentPage />,
  },
  {
    path: "/category/:slug",
    element: <ChildCategory />,
  },
  {
    path: "/category/view/:slug",
    element: <ChildCategory />,
  },
  {
    path: "/genre/:slug",
    element: <ChildGenre />,
  },
  {
    path: "/categories",
    element: <CategoriesPage />,
  },
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/movie",
    element: <MovieHome />,
  },
  {
    path: "/movie/:slug",
    element: <MoviePage />,
  },
  {
    path: "/animation/:slug",
    element: <MoviePage />,
  },
  {
    path: "/music/:slug",
    element: <MoviePage />,
  },
  {
    path: "/music",
    element: <MusicHome />,
  },
  {
    path: "/series",
    element: <SeriesHome />,
  },
  {
    path: "/series/:slug",
    element: <SeriesPage />,
  },
  {
    path: "/program/:slug",
    element: <ProgramPage />,
  },
  {
    path: "/live/:slug",
    element: <LivePage />,
  },
  {
    path: "/sport/:slug",
    element: <LivePage />,
  },
  {
    path: "/profile",
    element: <ProfilePage />,
  },
  {
    path: "/profile/favourites",
    element: <ProfileFavourtiesPage />,
  },
  {
    path: "/profile/subscriptions",
    element: <ProfileSubscriptionsPage />,
  },
  {
    path: "/profile/watch-history",
    element: <ProfileWatchHistoryPage />,
  },
  {
    path: "/search",
    element: <SearchPage />,
  },
  {
    path: "/subscription",
    element: <SubscriptionPage />,
  },
  {
    path: "/trailer/:slug",
    element: <TrailerPlayer />,
  },
  {
    path: "/watch/:slug",
    element: <MoviePlayer />,
  },
  {
    path: "/watch/:slug/:s/:e",
    element: <MoviePlayer />,
  },
  {
    path: "/watch/onlinebakhish/:contentSlug/:isTicketing",
    element: <MoviePlayer />,
  },
  {
    path: "/moftabakh",
    element: <MoftabakhPage />,
  },
  {
    path: "/online-cinema",
    element: <OnlineCinemaPage />,
  },
  {
    path: "/artists/:slug",
    element: <ArtistPage />,
  },
  {
    path: "/offline",
    element: <OfflinePage />,
  },
]);

// Offline page component
function OfflinePage() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: '#121212',
      color: '#fff',
      fontFamily: 'AnjomanMax, sans-serif',
      direction: 'rtl',
      padding: 40,
      textAlign: 'center',
    }}>
      <h1 style={{ fontSize: 32, marginBottom: 16 }}>اتصال به اینترنت برقرار نیست</h1>
      <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.7)', marginBottom: 32 }}>
        لطفاً اتصال اینترنت خود را بررسی کنید
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '16px 48px',
          background: '#850000',
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          fontSize: 18,
          cursor: 'pointer',
        }}
      >
        تلاش مجدد
      </button>
    </div>
  );
}

// App wrapper with offline detection and version check
function App() {
  const [offline, setOffline] = useState(isOffline());
  const [needsUpdate, setNeedsUpdate] = useState(false);

  // Check version on mount
  useEffect(() => {
    checkMinVersion().then((outdated) => {
      setNeedsUpdate(outdated);
    });
  }, []);

  // Listen for network status changes
  useEffect(() => {
    onNetworkStatusChange((online) => {
      setOffline(!online);
      console.log('[App] Network status:', online ? 'online' : 'offline');
    });
  }, []);

  // Show force update modal if version is outdated
  if (needsUpdate) {
    return <ForceUpdateModal />;
  }

  return (
    <StateProvider>
      <InputModeProvider>
        <PreloadSplash>
          {offline && !window.location.pathname.includes('/offline') ? (
            <OfflinePage />
          ) : (
            <RouterProvider router={router} />
          )}
        </PreloadSplash>
      </InputModeProvider>
    </StateProvider>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

